import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getMothers, getMotherInfo } from './api';

const MonthlySavings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [savingsData, setSavingsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const mothersResponse = await getMothers();
        // Ensure mothersResponse is an array
        const mothersArray = Array.isArray(mothersResponse) ? mothersResponse : (mothersResponse?.mothers || mothersResponse?.data || []);
        const infoPromises = mothersArray.map(mother => getMotherInfo(mother.generated_id));
        const infoResponses = await Promise.all(infoPromises);

        const enrichedData = mothersArray.map((mother, index) => {
          const info = infoResponses[index];
          const monthlySavings = {};
          Object.entries(info.monthly_data || {}).forEach(([monthKey, data]) => {
            const monthName = new Date(monthKey).toLocaleString('default', { month: 'long', year: 'numeric' });
            monthlySavings[monthName] = data.savings || 0;
          });
          return {
            ...mother,
            total_savings: info.total_savings || 0,
            monthly_savings: monthlySavings,
            mother_name: `${mother.first_name} ${mother.surname}`,
          };
        });

        setSavingsData(enrichedData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalSavings = savingsData.reduce((sum, m) => sum + (m.total_savings || 0), 0);
  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
  const currentMonthSavings = savingsData.reduce((sum, m) => sum + (m.monthly_savings?.[currentMonth] || 0), 0);
  const motherWithHighestSavings = savingsData.reduce((max, curr) =>
    (max.total_savings || 0) > (curr.total_savings || 0) ? max : curr, { mother_name: 'N/A', total_savings: 0 });
  const monthlySavingsByMonth = savingsData.reduce((acc, m) => {
    Object.entries(m.monthly_savings || {}).forEach(([month, amount]) => {
      acc[month] = (acc[month] || 0) + amount;
    });
    return acc;
  }, {});
  const monthWithHighestSavings = Object.entries(monthlySavingsByMonth).reduce((max, curr) =>
    (max[1] || 0) > (curr[1] || 0) ? max : curr, ['N/A', 0]);

  const barGraphData = {
    chart: { type: 'bar', height: 300 },
    title: { text: 'Total Savings per Month', style: { color: '#1a2e5c', fontSize: '16px' } },
    xAxis: {
      categories: Array.from({ length: 6 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - (5 - i));
        return date.toLocaleString('default', { month: 'short', year: 'numeric' });
      }),
      labels: { style: { fontSize: '10px', color: '#555' } },
    },
    yAxis: { title: { text: 'Savings ($)', style: { color: '#1a2e5c' } }, allowDecimals: false },
    series: [{
      name: 'Total Savings',
      data: Array.from({ length: 6 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - (5 - i));
        const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        return monthlySavingsByMonth[monthYear] || 0;
      }),
      color: '#ff6f61',
    }],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { bar: { borderColor: '#e63946', borderWidth: 1 } },
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, background: '#f5f5f5', minHeight: '100vh', width: '100%' }}>
      <Typography variant="h4" sx={{ color: '#1a2e5c', fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
        
      </Typography>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress sx={{ color: '#1a2e5c' }} /></Box>}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {!loading && !error && (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <StatCard 
              title="Total Savings" 
              value={`$${totalSavings.toLocaleString()}`} 
              icon={<AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="Accumulated by all mothers"
            />
            <StatCard 
              title="Current Month" 
              value={`$${currentMonthSavings.toLocaleString()}`} 
              icon={<AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
              description="This month's savings"
            />
            <StatCard 
              title="Highest Savings" 
              value={`$${motherWithHighestSavings.total_savings.toLocaleString()}`} 
              icon={<PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description={`${motherWithHighestSavings.mother_name}`}
            />
            <StatCard 
              title="Top Month" 
              value={`$${monthWithHighestSavings[1].toLocaleString()}`} 
              icon={<CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description={`${monthWithHighestSavings[0]}`}
            />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <HighchartsReact highcharts={Highcharts} options={barGraphData} />
            </Paper>
          </Box>

          <Accordion sx={{ borderRadius: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Mothers' Savings Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper sx={{ p: 2, borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Rank</strong></TableCell>
                      <TableCell><strong>Mother</strong></TableCell>
                      <TableCell><strong>Total Savings ($)</strong></TableCell>
                      <TableCell><strong>Activities</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savingsData
                      .sort((a, b) => (b.total_savings || 0) - (a.total_savings || 0))
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((mother, index) => (
                        <TableRow key={mother.generated_id || index}>
                          <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                          <TableCell>{mother.mother_name || 'N/A'}</TableCell>
                          <TableCell>{`$${mother.total_savings?.toLocaleString() || 0}`}</TableCell>
                          <TableCell>{(mother.activities || []).length}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={savingsData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
};

const StatCard = ({ title, value, icon, color, description }) => {
  return (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
      background: color,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      flex: '1 1 250px',
      minWidth: '250px',
      maxWidth: '100%',
    }}>
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
            {value}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default MonthlySavings;
